import {ContentEditableHtmlEditorPlugin} from '@frontapp/editor-html';

import {
  decodeBase64ToUint8Array,
  tryExtractBase64fromDataUrl,
  tryExtractTypeFromDataUrl,
} from '../../../../../libs/shared/core/src/helpers/url/dataUrlHelpers';
import {randomString} from '../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';

export function createInlineImageFromTextPlugin(
  onImagesAndHtmlPaste: (definition: InlineImagesHtmlDefinition) => Promise<void>,
): ContentEditableHtmlEditorPlugin {
  return {
    handleDataTransfer: (dataTransfer: DataTransfer) => {
      const inlineImages = findInlineImagesInDataTransfer(dataTransfer);

      if (!inlineImages) {
        return false;
      }

      onImagesAndHtmlPaste(inlineImages);
      return true;
    },
  };
}

const allowedImagesFormats = ['png', 'jpeg', 'jpg', 'gif'];
const dataURIRegex = new RegExp(`^data:image/(${allowedImagesFormats.join('|')});base64,`, 'i');

export interface InlineImagesHtmlDefinition {
  document: Document;
  images: ImagesDefinition[];
}

interface ImagesDefinition {
  id: string;
  file: File;
}

function findInlineImagesInDataTransfer(dataTransfer: DataTransfer): InlineImagesHtmlDefinition | null {
  const existingFiles = dataTransfer.files;

  // If we already have files, nothing to do.
  if (existingFiles.length > 0) {
    return null;
  }
  const htmlItem = dataTransfer.getData('text/html');

  if (!htmlItem) {
    return null;
  }

  const document = new DOMParser().parseFromString(htmlItem, 'text/html');
  const filesFromBase64Images = Array.from(document.querySelectorAll('img'))
    .filter((i) => i.src.match(dataURIRegex))
    .map((i) => ({
      data: tryExtractBase64fromDataUrl(i.src),
      type: tryExtractTypeFromDataUrl(i.src),
      node: i,
    }))
    .filter((i): i is {data: string; type: string; node: HTMLImageElement} => Boolean(i.data && i.type))
    .map((i) => {
      const id = `front-composer-${randomString()}`;
      const file = new File([decodeBase64ToUint8Array(i.data)], `${id}.${i.type?.split('/')[1]}`, {
        type: i.type,
      });

      i.node.classList.add(id);

      return {
        file,
        id,
      };
    });

  if (!filesFromBase64Images.length) {
    return null;
  }

  return {
    document,
    images: filesFromBase64Images,
  };
}
